import { BodyText } from '@/components/body-text';
import { GoonAvatar } from '@/components/goon-avatar';
import { HoverCardWithLink } from '@/components/hover-card-with-link';
import { RoundedBadge } from '@/components/rounded-badge';
import { RoundedButton } from '@/components/rounded-button';
import { DescriptionText } from '@/components/text/description';
import { DAILY_SPIN_BANNER_ID, dailySpinChainId } from '@/constants/shared';
import { useAuth } from '@/hooks/useAuth';
import { useDailySpinOffchain } from '@/hooks/useDailySpinOffchain';
import { useDailySpinOnchain } from '@/hooks/useDailySpinOnchain';
import { useHomeBanner } from '@/hooks/useHomeBanner';
import { DAILY_SPIN_ABI } from '@/network/abi/daily-spin';
import { RewardRecord } from '@/types';
import { truncateAddress } from '@/utils';
import {
  Box,
  Card,
  FormatNumber,
  Heading,
  HStack,
  Image,
  Portal,
  Spinner,
  Stack,
  Text,
  Toast,
  Toaster,
  VStack,
} from '@chakra-ui/react';
import { LottieReact } from '@lottielab/lottie-player';
import { useEffect, useState } from 'react';
import { PiSealQuestionBold } from 'react-icons/pi';
import { useNavigate } from 'react-router';
import { useReadContract } from 'wagmi';
import { AboutRewardsModal } from './AboutRewardsModal';
import { CostPill } from './CostPill';
import { DailySpinHistory } from './DailySpinHistory';
import { FortuneWheel } from './FortuneWheel';
import { Raffle } from './Raffle';
import { dailySpinToaster } from './constants';

export const DailySpinToaster = () => {
  return (
    <Portal>
      <Toaster toaster={dailySpinToaster} insetInline={{ mdDown: '4' }}>
        {(toast) => (
          <Toast.Root
            width={{ base: '100%', md: 'sm' }}
            rounded="12px"
            px={4}
            py={3}
            bg="#111"
            display="flex"
            alignItems="center"
            gap={2.5}
          >
            {toast.type === 'error' && (
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M17.6453 8.03281C17.3508 7.725 17.0461 7.40781 16.9312 7.12891C16.825 6.87344 16.8187 6.45 16.8125 6.03984C16.8008 5.27734 16.7883 4.41328 16.1875 3.8125C15.5867 3.21172 14.7227 3.19922 13.9602 3.1875C13.55 3.18125 13.1266 3.175 12.8711 3.06875C12.593 2.95391 12.275 2.64922 11.9672 2.35469C11.4281 1.83672 10.8156 1.25 10 1.25C9.18437 1.25 8.57266 1.83672 8.03281 2.35469C7.725 2.64922 7.40781 2.95391 7.12891 3.06875C6.875 3.175 6.45 3.18125 6.03984 3.1875C5.27734 3.19922 4.41328 3.21172 3.8125 3.8125C3.21172 4.41328 3.20312 5.27734 3.1875 6.03984C3.18125 6.45 3.175 6.87344 3.06875 7.12891C2.95391 7.40703 2.64922 7.725 2.35469 8.03281C1.83672 8.57188 1.25 9.18437 1.25 10C1.25 10.8156 1.83672 11.4273 2.35469 11.9672C2.64922 12.275 2.95391 12.5922 3.06875 12.8711C3.175 13.1266 3.18125 13.55 3.1875 13.9602C3.19922 14.7227 3.21172 15.5867 3.8125 16.1875C4.41328 16.7883 5.27734 16.8008 6.03984 16.8125C6.45 16.8187 6.87344 16.825 7.12891 16.9312C7.40703 17.0461 7.725 17.3508 8.03281 17.6453C8.57188 18.1633 9.18437 18.75 10 18.75C10.8156 18.75 11.4273 18.1633 11.9672 17.6453C12.275 17.3508 12.5922 17.0461 12.8711 16.9312C13.1266 16.825 13.55 16.8187 13.9602 16.8125C14.7227 16.8008 15.5867 16.7883 16.1875 16.1875C16.7883 15.5867 16.8008 14.7227 16.8125 13.9602C16.8187 13.55 16.825 13.1266 16.9312 12.8711C17.0461 12.593 17.3508 12.275 17.6453 11.9672C18.1633 11.4281 18.75 10.8156 18.75 10C18.75 9.18437 18.1633 8.57266 17.6453 8.03281ZM9.375 6.25C9.375 6.08424 9.44085 5.92527 9.55806 5.80806C9.67527 5.69085 9.83424 5.625 10 5.625C10.1658 5.625 10.3247 5.69085 10.4419 5.80806C10.5592 5.92527 10.625 6.08424 10.625 6.25V10.625C10.625 10.7908 10.5592 10.9497 10.4419 11.0669C10.3247 11.1842 10.1658 11.25 10 11.25C9.83424 11.25 9.67527 11.1842 9.55806 11.0669C9.44085 10.9497 9.375 10.7908 9.375 10.625V6.25ZM10 14.375C9.81458 14.375 9.63332 14.32 9.47915 14.217C9.32498 14.114 9.20482 13.9676 9.13386 13.7963C9.06291 13.625 9.04434 13.4365 9.08051 13.2546C9.11669 13.0727 9.20598 12.9057 9.33709 12.7746C9.4682 12.6435 9.63525 12.5542 9.8171 12.518C9.99896 12.4818 10.1875 12.5004 10.3588 12.5714C10.5301 12.6423 10.6765 12.7625 10.7795 12.9167C10.8825 13.0708 10.9375 13.2521 10.9375 13.4375C10.9375 13.6861 10.8387 13.9246 10.6629 14.1004C10.4871 14.2762 10.2486 14.375 10 14.375Z"
                  fill="white"
                />
              </svg>
            )}
            <Stack gap={0} flex="1" maxWidth="100%">
              {toast.title && <Text fontWeight="500">{toast.title}</Text>}
              {toast.description && (
                <Text fontWeight="400" fontSize="sm" color="rgba(255,255,255,0.7)">
                  {toast.description}
                </Text>
              )}
            </Stack>
            {toast.meta?.closable && <Toast.CloseTrigger />}
          </Toast.Root>
        )}
      </Toaster>
    </Portal>
  );
};

export const DailySpin = () => {
  const { dailySpinStreak } = useDailySpinOnchain();
  const { dismissBannerId } = useHomeBanner();
  const { address } = useAuth();
  const [localRecentSpin, setLocalRecentSpin] = useState<RewardRecord | null>(null);

  const { spinHistory, raffleUserPrizeData, isLoading: isLoadingSpinHistory } = useDailySpinOffchain();

  useEffect(() => {
    dismissBannerId(DAILY_SPIN_BANNER_ID);
  }, [dismissBannerId]);

  useEffect(() => {
    // Reset the local spin when wallet changes
    setLocalRecentSpin(null);
  }, [address]);

  const dailySpinHistory = (
    <VStack gap={4} alignItems="start" w="full" flex={1}>
      <Box>
        <HStack justifyContent="space-between" w="full" alignItems="center">
          <Heading mt={2} size="2xl">
            My Spins
          </Heading>
        </HStack>
        <DescriptionText fontSize="lg" fontFamily="heading">
          All spin costs go back into rewards
        </DescriptionText>
      </Box>
      <DailySpinHistory
        limit={100}
        data={spinHistory ?? []}
        localRecentSpin={localRecentSpin}
        isLoading={isLoadingSpinHistory}
      />
    </VStack>
  );

  return (
    <VStack gap={12} w="full">
      <Stack gap={12} direction={['column', 'column', 'column', 'column', 'row']} w="full">
        <VStack flex={1} alignItems={'start'} gap={6}>
          <HStack gap={4} alignItems="center">
            <VStack alignItems="start">
              <Heading size="4xl" fontWeight="700" display="flex" alignItems="center" gap={2}>
                The Daily Spin
                <RoundedBadge py={1.5} px={4} hideBelow="md">
                  <Image src="/assets/daily-spin/lightning-bolt-streak.svg" /> {dailySpinStreak} Day Streak
                </RoundedBadge>
              </Heading>
              <Box hideFrom="md">
                <CostPill />
              </Box>
            </VStack>
          </HStack>
          <Box hideBelow="md" w="full">
            <JackpotCard />
          </Box>
          <Box w="full" h="full" flex={1} display="flex" flexDirection="column" hideBelow="md">
            {dailySpinHistory}
          </Box>
        </VStack>
        <Box flex={1}>
          <FortuneWheel
            mostRecentReward={spinHistory?.[0]}
            recentRewardIsLoading={isLoadingSpinHistory}
            setLocalRecentSpin={setLocalRecentSpin}
          />
        </Box>
        <Box hideFrom="md" mt={-6}>
          <JackpotCard />
        </Box>
        <Box hideFrom="md">{dailySpinHistory}</Box>
      </Stack>
      <Raffle raffleUserPrizeData={raffleUserPrizeData ?? []} />
      <DailySpinToaster />
    </VStack>
  );
};

const JackpotCard = () => {
  const [isRewardsModalOpen, setIsRewardsModalOpen] = useState(false);
  const { data, isLoading: isLoadingJackpotPrize } = useReadContract({
    address: import.meta.env.VITE_DAILY_SPIN_CONTRACT_ADDRESS,
    abi: DAILY_SPIN_ABI,
    chainId: dailySpinChainId,
    functionName: 'getWeeklyJackpot',
    query: {
      refetchOnMount: false,
    },
  });

  const navigate = useNavigate();

  const { lastWeekRaffleWinner } = useDailySpinOffchain();

  // data is an array of [weekNumber, jackpotPrize, requiredStreak] - all values need to be parsed
  const [, jackpotPrize, requiredStreak] = data ?? [];
  const [isHovering, setIsHovering] = useState(false);
  return (
    <HoverCardWithLink
      href={null}
      cardRootStyles={{
        cursor: 'pointer',
        borderRadius: '2xl',
        overflow: 'hidden',
        bgColor: '#390E28',
        w: 'full',
        onMouseEnter: () => {
          setIsHovering(true);
        },
        onMouseLeave: () => {
          setIsHovering(false);
        },
      }}
    >
      <Card.Body
        color="fg.inverted"
        position="relative"
        onClick={() => {
          setIsRewardsModalOpen(true);
        }}
      >
        <Box position="absolute" inset={0} zIndex={0}>
          <LottieReact
            src="https://cdn.lottielab.com/l/DJbyGoaXLLDwy9.json"
            style={{
              position: 'absolute',
              minHeight: '100%',
              minWidth: '100%',
              aspectRatio: 1024 / 360,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            playing={isHovering}
          />
        </Box>
        <Heading color="fg.inverted" position="relative" zIndex={1}>
          This week's prize:{' '}
          {isLoadingJackpotPrize ? (
            <Spinner />
          ) : (
            <FormatNumber value={Number(jackpotPrize)} notation="compact" compactDisplay="short" />
          )}{' '}
          PLUME{' '}
        </Heading>
        <Text fontWeight="600" opacity=".7" mb={4}>
          Maintain a {isLoadingJackpotPrize ? <Spinner /> : requiredStreak}-day spin streak to qualify!
        </Text>
        {/* <Text opacity=".7" mb={4}>
          Your chances increase as the week goes on, ending at Sunday midnight UTC.
        </Text> */}
        <RoundedButton bgColor="#703F5C" _hover={{ opacity: '0.8' }} maxW={200} h={12}>
          <PiSealQuestionBold />
          Learn About Rewards
        </RoundedButton>
        <AboutRewardsModal isOpen={isRewardsModalOpen} onClose={() => setIsRewardsModalOpen(false)} />
      </Card.Body>
      {lastWeekRaffleWinner && (
        <Card.Footer
          py={4}
          bgColor="#390E28"
          justifyContent="space-between"
          // This is a hack to make the footer and body cards fit
          // The body card with lottie fills up a different amount of space than the lottie base file specifies
          // which makes it so the absolutely positioned div consumes a different amount of space than what it appears to
          // This creates overlap in *some* views, so we need to account for that.
          mt={[0, 0, 0, 0, 3]}
          onClick={() => navigate(`/profile/${lastWeekRaffleWinner.walletAddress}`)}
        >
          <Box>
            <BodyText color="fg.inverted" fontSize="md">
              Last week's winner
            </BodyText>
            <BodyText color="#FFFFFF99" fontSize="sm">
              <FormatNumber value={lastWeekRaffleWinner.rewardAmount} notation="compact" /> PLUME
            </BodyText>
          </Box>
          <RoundedButton bgColor="#FFFFFF1A">
            <GoonAvatar boxSize={6} seed={lastWeekRaffleWinner.walletAddress} />
            {truncateAddress(lastWeekRaffleWinner.walletAddress)}
          </RoundedButton>
        </Card.Footer>
      )}
    </HoverCardWithLink>
  );
};
